import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { fmtDate, fmtTime } from '../utils/datetime'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'

interface Credential {
  id: string
  username: string
  enabled: boolean
  description?: string
  created_at: string
}

export default function MetricsSettings() {
  const isAdmin = useIsAdmin()
  const [snapshot, setSnapshot] = useState<string | null>(null)
  const [snapshotAt, setSnapshotAt] = useState<Date | null>(null)
  const [snapLoading, setSnapLoading] = useState(false)
  const [snapshotError, setSnapshotError] = useState<string | null>(null)
  const snapshotLines = snapshot ? snapshot.trimEnd().split('\n').length : null

  const loadSnapshot = () => {
    setSnapLoading(true)
    setSnapshotError(null)
    api.getMetricsSnapshot()
      .then(text => { setSnapshot(text); setSnapshotAt(new Date()) })
      .catch((e: any) => setSnapshotError(e.message || 'Could not fetch the metrics snapshot'))
      .finally(() => setSnapLoading(false))
  }
  const [credentials, setCredentials] = useState<Credential[]>([])
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({ username: '', password: '', description: '' })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = () => {
    fetch('/api/settings/metrics-credentials').then(r => r.json()).then(setCredentials).finally(() => setLoading(false))
  }

  useEffect(load, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const res = await fetch('/api/settings/metrics-credentials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    })
    if (res.ok) {
      setForm({ username: '', password: '', description: '' })
      setShowAdd(false)
      load()
    } else {
      const body = await res.json()
      setError(body.detail || 'Failed to create credential')
    }
  }

  const handleToggle = async (id: string, enabled: boolean) => {
    await fetch(`/api/settings/metrics-credentials/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !enabled }),
    })
    load()
  }

  const handleDelete = async (id: string, username: string) => {
    if (!confirm(`Delete metrics credential "${username}"? Prometheus will lose access.`)) return
    await fetch(`/api/settings/metrics-credentials/${id}`, { method: 'DELETE' })
    load()
  }

  if (loading) return <div>Loading...</div>
  if (!isAdmin) {
    return (
      <div>
        <h2 style={{ marginTop: 0 }}>Metrics Credentials</h2>
        <AdminOnly what="Metrics credential management" />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '700px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Metrics Credentials</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#9aa0a6', fontSize: '0.85rem' }}>
            Manage Basic Auth credentials for the <code>/metrics</code> endpoint. Prometheus uses these to authenticate.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} style={{ padding: '0.5rem 1rem', background: '#FF4133', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          + Add Credential
        </button>
      </div>

      {error && <div style={{ background: '#3a1e1e', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem', color: '#ef9a9a', border: '1px solid #5a2a2a' }}>{error}</div>}

      {showAdd && (
        <form onSubmit={handleCreate} style={{ background: '#1e1e1e', padding: '1.2rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', border: '1px solid #333', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.95rem' }}>New Metrics Credential</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#9aa0a6', marginBottom: '0.2rem' }}>Username</label>
              <input value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required style={inputStyle} placeholder="prometheus" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#9aa0a6', marginBottom: '0.2rem' }}>Password (min 8 chars)</label>
              <input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required type="password" minLength={8} style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', color: '#9aa0a6', marginBottom: '0.2rem' }}>Description (optional)</label>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={inputStyle} placeholder="Production Prometheus scraper" />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" style={{ padding: '0.5rem 1rem', background: '#FF4133', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Create</button>
            <button type="button" onClick={() => setShowAdd(false)} style={{ padding: '0.5rem 1rem', border: '1px solid #555', background: '#1e1e1e', color: '#f5f5f5', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          </div>
        </form>
      )}

      <div style={{ background: '#1e1e1e', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', border: '1px solid #333', overflow: 'hidden' }}>
        {credentials.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#9aa0a6' }}>
            No credentials configured. The <code>/metrics</code> endpoint will return 401 to all requests.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#262626', textAlign: 'left' }}>
                <th style={{ padding: '0.6rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Username</th>
                <th style={{ padding: '0.6rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Description</th>
                <th style={{ padding: '0.6rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Status</th>
                <th style={{ padding: '0.6rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Created</th>
                <th style={{ padding: '0.6rem 1rem', fontSize: '0.8rem', color: '#9aa0a6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {credentials.map(cred => (
                <tr key={cred.id} style={{ borderTop: '1px solid #333' }}>
                  <td style={{ padding: '0.6rem 1rem', fontFamily: 'monospace' }}>{cred.username}</td>
                  <td style={{ padding: '0.6rem 1rem', fontSize: '0.85rem', color: '#9aa0a6' }}>{cred.description || '—'}</td>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    <span style={{ color: cred.enabled ? '#4caf50' : '#9aa0a6', fontSize: '0.85rem' }}>
                      {cred.enabled ? '● Active' : '○ Disabled'}
                    </span>
                  </td>
                  <td style={{ padding: '0.6rem 1rem', fontSize: '0.8rem' }}>{fmtDate(cred.created_at)}</td>
                  <td style={{ padding: '0.6rem 1rem' }}>
                    <button onClick={() => handleToggle(cred.id, cred.enabled)} style={{ marginRight: '0.5rem', padding: '0.25rem 0.5rem', border: '1px solid #555', background: '#1e1e1e', color: '#f5f5f5', borderRadius: '3px', cursor: 'pointer', fontSize: '0.8rem' }}>
                      {cred.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button onClick={() => handleDelete(cred.id, cred.username)} style={{ padding: '0.25rem 0.5rem', border: '1px solid #ef5350', background: '#1e1e1e', color: '#ef5350', borderRadius: '3px', cursor: 'pointer', fontSize: '0.8rem' }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: '1.5rem', background: '#1a2a3a', padding: '1rem', borderRadius: '4px', fontSize: '0.85rem', border: '1px solid #24384a' }}>
        <strong>Prometheus configuration:</strong>
        <pre style={{ margin: '0.5rem 0 0', background: '#262626', color: '#f5f5f5', padding: '0.75rem', borderRadius: '4px', fontSize: '0.8rem' }}>
{`scrape_configs:
  - job_name: 'e2e-fax-scheduler'
    metrics_path: '/metrics'
    basic_auth:
      username: '<username>'
      password: '<password>'
    static_configs:
      - targets: ['${window.location.host}']`}
        </pre>
      </div>

      {/* Live snapshot of what a scrape would return. Uses an authenticated
          /api/settings/metrics-snapshot route rather than /metrics, because
          /metrics is Basic-Auth guarded for Prometheus and the browser cannot
          present those credentials from a fetch. */}
      <div style={{ marginTop: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          <h3 style={{ margin: 0 }}>Current metrics output</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            {snapshotAt && (
              <span style={{ fontSize: '0.75rem', color: '#9aa0a6' }}>
                fetched {fmtTime(snapshotAt)}
                {snapshotLines !== null ? ` · ${snapshotLines} lines` : ''}
              </span>
            )}
            <button
              onClick={loadSnapshot}
              disabled={snapLoading}
              style={{ padding: '0.35rem 0.8rem', fontSize: '0.8rem', background: '#262626', color: '#f5f5f5', border: '1px solid #333', borderRadius: '4px', cursor: snapLoading ? 'not-allowed' : 'pointer' }}
            >
              {snapLoading ? 'Fetching…' : snapshot ? 'Refresh' : 'Load snapshot'}
            </button>
          </div>
        </div>

        <p style={{ margin: '0 0 0.6rem', fontSize: '0.78rem', color: '#9aa0a6', lineHeight: 1.5 }}>
          Exactly what Prometheus receives from <code>/metrics</code>. Note this is a
          <strong> per-process</strong> snapshot: the registry is per gunicorn worker, so the
          <code> process_*</code> series reflect whichever worker served this request. The
          <code> e2e_fax_job_*</code> series are DB-derived and identical across workers.
        </p>

        {snapshotError && (
          <p style={{ margin: '0 0 0.6rem', fontSize: '0.8rem', color: '#ef5350' }}>{snapshotError}</p>
        )}

        {snapshot && (
          <pre style={{ margin: 0, background: '#262626', color: '#f5f5f5', border: '1px solid #333', padding: '0.75rem', borderRadius: '4px', fontSize: '0.72rem', maxHeight: '420px', overflow: 'auto', whiteSpace: 'pre' }}>
            {snapshot}
          </pre>
        )}
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '0.5rem', background: '#1e1e1e', color: '#f5f5f5', border: '1px solid #333', borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box' }
